import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../Context/TenantContext';
import { hasAccess, formatTimeLeft } from '../utils/subscription';
import {
 ClockIcon,
 ShieldCheckIcon,
 ExclamationTriangleIcon,
 StarIcon
} from '@heroicons/react/24/outline';

const SubscriptionGuard = ({ children }) => {
 const navigate = useNavigate();
 const { tenant } = useTenant();
 const [accessStatus, setAccessStatus] = useState(null);
 const [isLoading, setIsLoading] = useState(true);

 useEffect(() => {
    const checkAccess = async () => {
      // BYPASS FOR TENANT STUDENTS
      const storedUser = localStorage.getItem('user');
      const user = JSON.parse(storedUser || '{}');
      
      const isMainPlatform = !tenant || 
                             tenant.domain === 'theskillgenie.com' || 
                             tenant.tenantKey === 'skillgenie' ||
                             window.location.hostname === 'theskillgenie.com' ||
                             window.location.hostname === 'www.theskillgenie.com' ||
                             window.location.hostname === 'localhost' ||
                             window.location.hostname === '127.0.0.1';

      // Robust check for Student Tenant
      // 1. If we are on a custom domain (tenant student)
      // 2. If user role is student and has a tenant ID or college ID
      const isStudentTenant = !isMainPlatform || (user && user.role === 'student' && (user.tenant || user.college_id || user.college || user.tenant_id));

      if (isStudentTenant) {
        // Double check login - if not logged in at all, redirect to root login
        const userId = user?.user_code || user?.user_id || user?.id || user?.userId || localStorage.getItem('user_id');
        if (!userId) {
          setIsLoading(false);
          navigate('/');
          return;
        }
        setAccessStatus({ hasAccess: true, accessType: 'full', daysLeft: 999 });
        setIsLoading(false);
        return;
      }
      
      const userId = user?.user_code || user?.user_id || user?.id || user?.userId || localStorage.getItem('user_id');

      if (!userId) {
        setIsLoading(false);
        // For main platform, redirect to subscription plans
        navigate('/subscription-plans');
        return;
      }
 
 const status = await hasAccess(userId);
 setAccessStatus(status);
 setIsLoading(false);

 // If no access, redirect to subscription plans page
 if (!status.hasAccess) {
   if (isMainPlatform) {
     navigate('/subscription-plans');
   } else {
     // Fallback for tenant users if something goes wrong with the bypass
     setAccessStatus({ hasAccess: true, accessType: 'full', daysLeft: 999 });
   }
 }
 };

 checkAccess();

 // Check access status every minute
 const interval = setInterval(checkAccess, 60000);

 return () => clearInterval(interval);
 }, [navigate]);

 if (isLoading) {
 return (
 <div className="min-h-screen bg-bgMain flex items-center justify-center">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
 <p className="text-muted">Checking access...</p>
 </div>
 </div>
 );
 }

 if (!accessStatus.hasAccess) {
 return (
 <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
 <div className="max-w-md w-full bg-bgSection rounded-2xl p-8 text-center border border-gray-700">
 <ExclamationTriangleIcon className="h-16 w-16 text-red-400 mx-auto mb-6" />
 <h2 className="text-2xl font-bold text-white mb-4">Access Expired</h2>
 <p className="text-muted mb-6">
 Your free trial has ended. Subscribe to continue accessing all features.
 </p>
 <button
 onClick={() => navigate('/subscription-plans')}
 className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary transition-colors"
 >
 Subscribe Now
 </button>
 </div>
 </div>
 );
 }

 // Show access status banner for trial users
 if (accessStatus.accessType === 'trial') {
 return (
 <div>
 {/* Trial Status Banner */}
 <div className="bg-gradient-to-r from-primary to-secondary text-white py-3 px-6">
   <div className="max-w-7xl mx-auto flex items-center justify-between">
     <div className="flex items-center gap-3">
       <ClockIcon className="h-5 w-5" />
       <span className="font-semibold text-white">
         Free Trial - {formatTimeLeft(accessStatus.daysLeft)}
       </span>
     </div>
     <button
       onClick={() => navigate('/subscription-plans')}
       className="bg-white text-primary px-4 py-1 rounded-lg text-sm font-bold hover:bg-blue-50 transition-colors"
     >
       Upgrade to Premium
     </button>
   </div>
 </div>
 {children}
 </div>
 );
 }

 // Show premium status banner for subscribed users
 if (accessStatus.accessType === 'subscription') {
 return (
 <div>
 {/* Premium Status Banner */}
 <div 
 className="bg-primary text-white py-3 px-6 cursor-pointer hover:bg-secondary transition-colors"
 onClick={() => navigate('/subscription-plans')}
 >
 <div className="max-w-7xl mx-auto flex items-center justify-between">
 <div className="flex items-center gap-3">
 <ShieldCheckIcon className="h-5 w-5" />
 <span className="font-semibold">
 Premium Access - {formatTimeLeft(accessStatus.daysLeft)}
 </span>
 </div>
 <div className="flex items-center gap-2">
 <StarIcon className="h-5 w-5" />
 <span className="text-sm font-semibold">Premium Member - Click to Manage/Extend</span>
 </div>
 </div>
 </div>
 {children}
 </div>
 );
 }

 return children;
};

export default SubscriptionGuard;



